import { useState } from 'react';
import type { OfferCampaignCodeResponse, OfferCampaignLookupResponse } from '../types/offer-campaigns';
import { OfferCountdownBadge } from './OfferCountdownBadge';
import { PatientEligibilityCard } from './EligibilityStep';
import { OfferVisualIcon } from './OfferVisualIcon';
import { formatOfferCampaignDateTime } from './OfferCampaignDateField';

export function AdvanceVerificationStep({
  lookup,
  code,
  busy,
  canEnroll,
  error,
  onVerify,
  onRetry,
  onBack,
  onCancel,
  canAdminOverride = false,
  onOpenAdminOverride,
}: {
  lookup: OfferCampaignLookupResponse;
  code: OfferCampaignCodeResponse;
  busy: boolean;
  canEnroll: boolean;
  error?: string | null;
  onVerify: () => void;
  onRetry: () => void;
  onBack: () => void;
  onCancel: () => void;
  canAdminOverride?: boolean;
  onOpenAdminOverride?: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copyOfferCode = async () => {
    try {
      await navigator.clipboard.writeText(code.offerCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="offer-campaign-work-panel offer-advance-card">
      <div className="offer-campaign-code-card offer-code-card">
        <OfferVisualIcon name="ticket" />
        <span>Offer Code</span>
        <div className="offer-code-value-row">
          <strong>{code.offerCode}</strong>
          <button className={copied ? 'offer-copy-code-button is-copied' : 'offer-copy-code-button'} type="button" onClick={copyOfferCode} aria-label="Copy offer code" title="Copy offer code">
            <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="8" y="8" width="11" height="11" rx="2" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" /></svg>
            <span>{copied ? 'Copied' : 'Copy code'}</span>
          </button>
        </div>
        <p>Collect the advance externally using this code, then verify the matching payment record.</p>
      </div>
      {error && <div className="offer-advance-error"><OfferVisualIcon name="warning" /><div><strong>Payment verification needs attention</strong><span>{error}</span></div></div>}
      <PatientEligibilityCard lookup={{ ...lookup, discountAmount: lookup.discountAmount ?? code.discountAmount, discountAmountFormatted: lookup.discountAmountFormatted ?? code.discountAmountFormatted }} />
      <div className="offer-campaign-verify-grid">
        <div><span>Required Advance Rule</span><strong>{friendlyAdvanceType(code.requiredAdvanceType)}</strong></div>
        <div><span>Required Advance Amount</span><strong>{currency(code.requiredAdvanceAmount)}</strong></div>
        <div><span>Reservation Countdown</span><OfferVisualIcon name="clock" /><OfferCountdownBadge expiresAt={code.reservationExpiresAt} /></div>
        {code.couponValidUntil && <div><span>Enrollment Validity</span><strong>{formatIstDateTime(code.couponValidUntil)}</strong><small><b>Select this Package End Date in Artisan.</b></small></div>}
      </div>
      <div className="offer-campaign-actions">
        {!error && <button className="btn btn-primary" type="button" onClick={onVerify} disabled={!canEnroll || busy}>{busy ? 'Verifying...' : 'Check Advance Now'}</button>}
        {error && <button className="btn btn-primary" type="button" onClick={onRetry} disabled={!canEnroll || busy}>{busy ? 'Verifying...' : 'Retry Advance Check'}</button>}
        <button className="btn btn-outline" type="button" onClick={onBack} disabled={busy}>Back</button>
        <button className="btn btn-outline" type="button" onClick={onCancel} disabled={busy}>Cancel Enrollment</button>
        {canAdminOverride && onOpenAdminOverride && <button className="btn btn-outline offer-admin-override-button" type="button" onClick={onOpenAdminOverride} disabled={busy}>Admin override</button>}
      </div>
    </div>
  );
}

function currency(value: number | null | undefined) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value);
}

function formatIstDateTime(value: string) {
  return formatOfferCampaignDateTime(value, true);
}

function friendlyAdvanceType(value: string | null | undefined) {
  const labels: Record<string, string> = {
    DISCOUNT_AMOUNT: 'Same as eligible discount',
    EXACT_AMOUNT: 'Exact amount',
    MINIMUM_AMOUNT: 'Minimum amount',
    RANGE: 'Approved amount range',
    IVF_DAY_ADVANCE: 'IVF booking advance',
  };
  return labels[String(value || '').trim().toUpperCase()] || (value ? value.replaceAll('_', ' ').toLowerCase() : '-');
}
